/**
 * @file ClinicSoftwareSettingsPage.tsx
 * @brief Company Admin → Settings → Clinic software. Link this login to the
 *        clinic software's doctor record (MEDYX today).
 *
 * @details
 * ⚠️ **No vendor name in the copy on this page.** The doctor is linking their
 * own account; which system their clinic runs is our integration's business,
 * and naming it dates the page the moment there is a second connector. MEDYX
 * stays named here, in the connector and in the docs. (19-09-2026)
 *
 * @details
 * `medyx_doctor_id` / `medyx_doctor_key` belong to the USER, not to any one
 * assessment: `export/gripXml.service.mjs` and `export/mlrReportInput.service.mjs`
 * both read them for every report this doctor produces. Until 14-09-2026 the
 * only place to set them was a drawer inside a single assessment's Tools —
 * a per-account setting reached through a per-case tool, and invisible until
 * you opened some case at random. It lives with the other settings now.
 *
 * @references
 * - BACKEND/src/features/medical-assessment/doctorLink.service.mjs
 * - docs/decisions/ADR-026-medical-assessment-module.md
 *
 * @author DES
 * @date 14-09-2026
 */

import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  GridBand,
  GridBlock,
  GridCell,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import { DoctorLinkPanel } from "./components/DoctorLinkPanel";

export function ClinicSoftwareSettingsPage() {
  return (
    <div className={gridPageBleedClass("pb-6")}>
      <GridBlock>
        <GridBand className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Button variant="ghost" size="icon" asChild aria-label="Back">
              <Link to="/company/settings">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div>
              <h2 className="text-xl font-semibold tracking-tight">
                Clinic software identity
              </h2>
              <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                Link this VERBLIZR login to your doctor record in your clinic
                software. Every MLR you export carries it — the GRIP XML names
                you as the examining doctor, and the report cover uses the same
                identity.
              </p>
            </div>
          </div>
        </GridBand>

        <GridCell className="lg:col-span-2">
          <DoctorLinkPanel />
        </GridCell>
      </GridBlock>
    </div>
  );
}
